import React, { useContext } from "react";
import VN from "../components/VN";
import { UserDataContext } from "../components/UserDataContext";
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Paper,
  Stack,
} from "@mui/material";

import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

const steps = [
  {
    number: 1,
    title: "Złóż wniosek do WZON",
    subtitle: "Wojewódzki Zespół ds. Orzekania o Niepełnosprawności",
    items: [
      "Wniosek możesz złożyć osobiście, przez pełnomocnika lub online.",
      "Do wniosku dołącz dokumentację medyczną i inne potrzebne załączniki.",
    ],
  },
  {
    number: 2,
    title: "Ocena potrzeby wsparcia",
    items: [
      "WZON przeprowadza ocenę sytuacji osoby ubiegającej się o świadczenie.",
      "Analizowana jest dokumentacja.",
      "Przeprowadzana jest rozmowa lub wywiad.",
      "Ustalany jest poziom potrzeby wsparcia.",
    ],
  },
  {
    number: 3,
    title: "Otrzymasz decyzję z WZON",
    items: [
      "Decyzja zawiera liczbę punktów określających poziom potrzeby wsparcia.",
      "Do uzyskania świadczenia potrzebne jest co najmniej 70 punktów.",
    ],
  },
  {
    number: 4,
    title: "Złóż wniosek do ZUS",
    items: [
      "Po otrzymaniu decyzji WZON możesz złożyć wniosek o świadczenie wspierające.",
      "Wniosek można złożyć elektronicznie lub przez pełnomocnika.",
      "Do wniosku potrzebna jest decyzja WZON.",
    ],
  },
  {
    number: 5,
    title: "ZUS rozpatruje wniosek",
    items: [
      "ZUS sprawdza przekazane dokumenty.",
      "Może poprosić o dodatkowe informacje.",
      "Po spełnieniu warunków świadczenie zostaje przyznane.",
    ],
  },
  {
    number: 6,
    title: "Otrzymujesz świadczenie",
    items: [
      "Świadczenie wypłacane jest co miesiąc przez ZUS.",
      "Wysokość świadczenia zależy od liczby punktów przyznanych przez WZON.",
    ],
  },
];

const supportLevels = [
  { points: "70–74 pkt", percentage: "40%" },
  { points: "75–79 pkt", percentage: "60%" },
  { points: "80–84 pkt", percentage: "80%" },
  { points: "85–89 pkt", percentage: "120%" },
  { points: "90–94 pkt", percentage: "180%" },
  { points: "95–100 pkt", percentage: "220%" },
];

const cardStyle = {
  width: "100%",
  maxWidth: "768px",
  border: "1px solid #f0f0f0",
  transition: "all .2s ease",
  "&:hover": {
    boxShadow: "0 10px 35px rgba(0,0,0,0.08)",
  },
};

export default function SwiadczenieWspierajace() {
  const { userData } = useContext(UserDataContext);

  return (
    <Box sx={{ backgroundColor: "#fafafa", minHeight: "100vh", pb: 6 }}>
      <VN userData={userData} />

      <Container maxWidth="md" sx={{ py: 4 }}>
        {/* NAGŁÓWEK */}
        <Box mb={5} textAlign="center">
          <Typography
            variant="h1"
            fontWeight={800}
            gutterBottom
            sx={{ color: "#1a1a1a", fontSize: { xs: "2rem", md: "2.75rem" } }}
          >
            Jak uzyskać świadczenie wspierające?
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ maxWidth: "700px", mx: "auto" }}
          >
            Zobacz krok po kroku, jak wygląda proces uzyskania świadczenia
            wspierającego – od wniosku do WZON aż do wypłaty świadczenia przez
            ZUS.
          </Typography>
        </Box>

        {/* JEDNA KOLUMNA - WYŚRODKOWANA */}
        <Stack spacing={3} alignItems="center">
          {/* SEKĊJA: KROKI PROCESU */}
          {steps.map((step, index) => (
            <React.Fragment key={step.number}>
              <Card sx={cardStyle}>
                <CardContent sx={{ p: 3 }}>
                  <Box display="flex" gap={2.5} alignItems="flex-start">
                    {/* Numer kroku */}
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: "50%",
                        bgcolor: "primary.main",
                        color: "white",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 700,
                        fontSize: "1.25rem",
                        flexShrink: 0,
                        boxShadow: "0 4px 10px rgba(25, 118, 210, 0.3)",
                      }}
                    >
                      {step.number}
                    </Box>

                    {/* Treść kroku */}
                    <Box flexGrow={1}>
                      <Typography
                        variant="h6"
                        fontWeight={700}
                        sx={{ color: "#2c3e50" }}
                      >
                        {step.title}
                      </Typography>

                      {step.subtitle && (
                        <Typography
                          variant="subtitle2"
                          color="primary"
                          sx={{ fontWeight: 600, mb: 1 }}
                        >
                          {step.subtitle}
                        </Typography>
                      )}

                      <List disablePadding sx={{ mt: 1 }}>
                        {step.items.map((item, i) => (
                          <ListItem key={i} disablePadding sx={{ mb: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 30 }}>
                              <CheckCircleOutlineIcon
                                color="success"
                                fontSize="small"
                              />
                            </ListItemIcon>
                            <ListItemText
                              primary={item}
                              primaryTypographyProps={{
                                variant: "body2",
                                color: "text.secondary",
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              {/* Strzałka w dół między krokami */}
              {index < steps.length - 1 && (
                <Box display="flex" justifyContent="center" my={-1}>
                  <ArrowDownwardIcon color="action" />
                </Box>
              )}
            </React.Fragment>
          ))}

          <Divider sx={{ width: "100%", maxWidth: 768, my: 2 }} />

          {/* KARTA: WAŻNE */}
          <Card
            sx={{
              ...cardStyle,
              borderLeft: "6px solid #ed6c02",
              bgcolor: "#fffaf0",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                <ErrorOutlineIcon sx={{ color: "#ed6c02" }} />
                <Typography variant="h6" fontWeight={700} color="#ed6c02">
                  Ważne
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" paragraph>
                Bez decyzji WZON określającej poziom potrzeby wsparcia nie można
                przejść do kolejnego etapu ubiegania się o świadczenie.
              </Typography>
              <Typography variant="subtitle2" fontWeight={700}>
                Komisja WZON ustala liczbę punktów.
              </Typography>
            </CardContent>
          </Card>

          {/* KARTA: SKALA PUNKTÓW */}
          <Card sx={cardStyle}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <FormatListNumberedIcon color="primary" />
                <Typography variant="h6" fontWeight={700}>
                  Skala punktów
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />

              <Stack spacing={1} mb={2}>
                {supportLevels.map((level) => (
                  <Box
                    key={level.points}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: "rgba(25, 118, 210, 0.04)",
                    }}
                  >
                    <Typography variant="body2" fontWeight={600}>
                      {level.points}
                    </Typography>
                    <Chip
                      label={level.percentage}
                      size="small"
                      color="primary"
                      sx={{ fontWeight: 700 }}
                    />
                  </Box>
                ))}
              </Stack>

              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                Procent oznacza część renty socjalnej stanowiącą podstawę
                obliczenia świadczenia.
              </Typography>
            </CardContent>
          </Card>

          {/* KARTA: PAMIĘTAJ */}
          <Card sx={{ ...cardStyle, bgcolor: "#f0f9ff" }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                <InfoOutlinedIcon color="primary" />
                <Typography variant="h6" fontWeight={700} color="primary.main">
                  Pamiętaj
                </Typography>
              </Box>
              <List disablePadding>
                {[
                  "Zadbaj o komplet dokumentacji medycznej.",
                  "Odpowiadaj na wezwania WZON i ZUS.",
                  "Sprawdzaj status swojego wniosku.",
                  "Od decyzji można się odwołać.",
                ].map((text, idx) => (
                  <ListItem key={idx} disablePadding sx={{ mb: 1 }}>
                    <ListItemIcon sx={{ minWidth: 24 }}>•</ListItemIcon>
                    <ListItemText
                      primary={text}
                      primaryTypographyProps={{ variant: "body2" }}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>

          {/* PODSUMOWANIE PROCESU */}
          <Paper sx={{ ...cardStyle, p: 4, textAlign: "center", mt: 2 }}>
            <Typography variant="h5" fontWeight={700} mb={3}>
              Cały proces w skrócie
            </Typography>

            <Box
              display="flex"
              flexWrap="wrap"
              justifyContent="center"
              alignItems="center"
              gap={2}
            >
              {[
                "Wniosek do WZON",
                "Ocena",
                "Decyzja",
                "Wniosek do ZUS",
                "Rozpatrzenie",
                "Wypłata",
              ].map((stepTitle, idx, arr) => (
                <React.Fragment key={idx}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      minWidth: 110,
                      bgcolor: "rgba(25, 118, 210, 0.05)",
                      border: "1px solid #e0e0e0",
                      borderRadius: 3,
                    }}
                  >
                    <Typography
                      variant="caption"
                      display="block"
                      fontWeight={800}
                      color="primary"
                    >
                      {idx + 1}
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {stepTitle}
                    </Typography>
                  </Paper>

                  {idx < arr.length - 1 && (
                    <ArrowForwardIcon
                      color="action"
                      sx={{ display: { xs: "none", sm: "block" } }}
                    />
                  )}
                </React.Fragment>
              ))}
            </Box>
          </Paper>
        </Stack>
      </Container>
    </Box>
  );
}
